/**
 * BoardV3 Helper Functions and Constants
 */

import type { Task } from '../../types';
import type { ScheduledTask } from '../scheduler/types';
import { getTaskDayIndex } from '../../lib/dateUtils';
import { getTaskDurationFromPert } from '../scheduler/taskDuration';

// ============================================
// Constants
// ============================================

export const DAYS_PER_WEEK = 5;
export const WEEKS_PER_YEAR = 52;
export const BASE_DAY_WIDTH = 32;
export const HEADER_ROW_HEIGHT = 20;

/** Locale-aware short month names (Jan, Feb, ...) */
export function getMonthNamesShort(locale: string): string[] {
  return Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(2024, i, 1))
  );
}

/** Locale-aware short day names for business days (Mon-Fri) */
export function getDayNamesShort(locale: string): string[] {
  // 2024-01-01 is a Monday
  return Array.from({ length: 5 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(new Date(2024, 0, 1 + i))
  );
}

/** @deprecated Use getMonthNamesShort(locale) instead */
export const MONTH_NAMES_DE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
/** @deprecated Use getDayNamesShort(locale) instead */
export const DAY_NAMES = ['Mo', 'Di', 'Mi', 'Do', 'Fr'];

// ============================================
// Helper Functions
// ============================================

/**
 * Calculate KW and year from absolute week index
 */
export function getKwAndYear(
  absoluteWeekIndex: number,
  startKW: number,
  startYear: number
): { kw: number; year: number } {
  const totalWeeksFromYearStart = (startKW - 1) + absoluteWeekIndex;
  const yearsOffset = Math.floor(totalWeeksFromYearStart / WEEKS_PER_YEAR);
  const kwInYear = (totalWeeksFromYearStart % WEEKS_PER_YEAR) + 1;

  return {
    kw: kwInYear,
    year: startYear + yearsOffset,
  };
}

/**
 * Calculate task duration based on PERT estimates.
 * Delegates to the canonical implementation in scheduler/taskDuration.
 * Kept for non-render code (splitTask, context menus) that doesn't have scheduler data.
 */
export function getTaskDuration(task: Task): number {
  return getTaskDurationFromPert(task);
}

/**
 * Get maximum row number from assignments map
 */
export function getMaxRow(assignments: Map<string, number> | undefined): number {
  if (!assignments || assignments.size === 0) return 0;
  let max = 0;
  for (const row of assignments.values()) {
    if (row > max) max = row;
  }
  return max;
}

// ============================================
// Gap Calculation
// ============================================

export interface TargetGaps {
  leftGap: number | null;
  rightGap: number | null;
  leftTaskTitle: string | null;
  rightTaskTitle: string | null;
}

/**
 * Calculate gaps at target position for drag info display.
 * Uses scheduledTasks map for durations when available.
 */
export function calculateGapsAtPosition(
  task: Task,
  targetColumn: string,
  targetDay: number,
  tasksByColumn: Record<string, Task[]>,
  scheduledTasks: Map<string, ScheduledTask>,
  boardStartDate?: string,
): TargetGaps {
  const duration = scheduledTasks.get(task.id)?.effectiveDays ?? getTaskDuration(task);
  const targetEnd = targetDay + duration;

  const columnTasks = tasksByColumn[targetColumn] || [];

  // Find closest task on the left (ends before or at targetDay)
  let leftGap: number | null = null;
  let leftTaskTitle: string | null = null;
  const leftTasks = columnTasks
    .filter(t => t.id !== task.id)
    .map(t => {
      const sDuration = scheduledTasks.get(t.id)?.effectiveDays ?? getTaskDuration(t);
      return { task: t, end: getTaskDayIndex(t.startDate, boardStartDate) + sDuration };
    })
    .filter(t => t.end <= targetDay);

  if (leftTasks.length > 0) {
    const closest = leftTasks.reduce((a, b) => a.end > b.end ? a : b);
    leftGap = targetDay - closest.end;
    leftTaskTitle = closest.task.title;
  }

  // Find closest task on the right (starts at or after targetEnd)
  let rightGap: number | null = null;
  let rightTaskTitle: string | null = null;
  const rightTasks = columnTasks
    .filter(t => t.id !== task.id)
    .map(t => ({ task: t, start: getTaskDayIndex(t.startDate, boardStartDate) }))
    .filter(t => t.start >= targetEnd);

  if (rightTasks.length > 0) {
    const closest = rightTasks.reduce((a, b) => a.start < b.start ? a : b);
    rightGap = closest.start - targetEnd;
    rightTaskTitle = closest.task.title;
  }

  return { leftGap, rightGap, leftTaskTitle, rightTaskTitle };
}
